// Builds a synthetic "daily warm-up" trin with one interleaved round
// covering everything the user has cleared so far. Visible from
// the Dashboard once they've finished grundniveau. Result lands in
// recentResults via addLessonResult({ skipProgression: true }) so the
// curriculum doesn't shift under their feet.

import { LEVEL_6_VALID, LEVEL_6_KEYS } from '../data/curriculum/grundniveau';
import {
  NIVEAU_1_SPRINT_WORDS,
  NIVEAU_1_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-1';
import {
  NIVEAU_2_SPRINT_WORDS,
  NIVEAU_2_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-2';
import {
  NIVEAU_3_SPRINT_WORDS,
  NIVEAU_3_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-3';
import {
  NIVEAU_4_ALPHABET_WORDS,
  NIVEAU_4_CONSOLIDATION_SENTENCE_DA,
  NIVEAU_4_LIST_SENTENCE_DA,
  NIVEAU_4_PERIOD_WORDS_DA,
  NIVEAU_4_PUNCTUATION_SENTENCE_DA,
  NIVEAU_4_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-4';
import {
  NIVEAU_4_CONSOLIDATION_SENTENCE_EN,
  NIVEAU_4_EN_ALPHABET_WORDS,
  NIVEAU_4_LIST_SENTENCE_EN,
  NIVEAU_4_PERIOD_WORDS_EN,
  NIVEAU_4_PUNCTUATION_SENTENCE_EN,
} from '../data/curriculum/niveau-4-en';
import {
  NIVEAU_5_CONSOLIDATION_SENTENCES,
  NIVEAU_5_MIXED_WORDS,
  NIVEAU_5_SPRINT_WORDS,
  NIVEAU_5_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-5';
import {
  NIVEAU_6_SPRINT_WORDS,
  NIVEAU_6_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-6';
import { filterWords, round, warmupBlended } from '../data/curriculum/generators';
import { effectiveNiveau4Lexicon } from './niveau4Lexicon';
import type { CurriculumTierId, Niveau4Lexicon, Trin, UserProfile } from '../types';

export const WARMUP_TRIN_ID = '__warmup__';

/** Andel af runden med tegnsætning / store bogstaver / sætninger. */
export const WARMUP_ADVANCED_SHARE = 0.2;

interface WarmupSource {
  chunks: string[];
  weight: number;
  unlockedKeys: string[];
}

const SOURCES: Record<string, WarmupSource> = {
  grundniveau: {
    chunks: LEVEL_6_VALID,
    weight: 1,
    unlockedKeys: [...LEVEL_6_KEYS, ' '],
  },
  'niveau-1': {
    chunks: NIVEAU_1_SPRINT_WORDS,
    weight: 1,
    unlockedKeys: NIVEAU_1_UNLOCKED_KEYS,
  },
  'niveau-2': {
    chunks: NIVEAU_2_SPRINT_WORDS,
    weight: 1,
    unlockedKeys: NIVEAU_2_UNLOCKED_KEYS,
  },
  'niveau-3': {
    chunks: NIVEAU_3_SPRINT_WORDS,
    weight: 2,
    unlockedKeys: NIVEAU_3_UNLOCKED_KEYS,
  },
  'niveau-4': {
    // Bland dansk + engelsk så c/x/z-ord er repræsenteret (warm-up er ikke enten/eller).
    chunks: [...NIVEAU_4_ALPHABET_WORDS, ...NIVEAU_4_EN_ALPHABET_WORDS],
    weight: 2,
    unlockedKeys: NIVEAU_4_UNLOCKED_KEYS,
  },
  'niveau-5': {
    chunks: NIVEAU_5_SPRINT_WORDS,
    weight: 2,
    unlockedKeys: NIVEAU_5_UNLOCKED_KEYS,
  },
  'niveau-6': {
    chunks: NIVEAU_6_SPRINT_WORDS,
    weight: 2,
    unlockedKeys: NIVEAU_6_UNLOCKED_KEYS,
  },
};

const TIER_ORDER = [
  'grundniveau',
  'niveau-1',
  'niveau-2',
  'niveau-3',
  'niveau-4',
  'niveau-5',
  'niveau-6',
] as const;

function splitOnSpaces(text: string): string[] {
  return text.trim().split(/\s+/).filter((c) => c.length > 0);
}

function splitSentences(...parts: string[]): string[] {
  return parts.flatMap((p) =>
    p
      .trim()
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
  );
}

function niveau4AdvancedChunks(lexicon: Niveau4Lexicon): string[] {
  if (lexicon === 'en') {
    return [
      ...splitOnSpaces(NIVEAU_4_PERIOD_WORDS_EN),
      NIVEAU_4_LIST_SENTENCE_EN,
      ...splitSentences(
        NIVEAU_4_PUNCTUATION_SENTENCE_EN,
        NIVEAU_4_CONSOLIDATION_SENTENCE_EN,
      ),
    ];
  }
  return [
    ...splitOnSpaces(NIVEAU_4_PERIOD_WORDS_DA),
    NIVEAU_4_LIST_SENTENCE_DA,
    ...splitSentences(
      NIVEAU_4_PUNCTUATION_SENTENCE_DA,
      NIVEAU_4_CONSOLIDATION_SENTENCE_DA,
    ),
  ];
}

/** Tegn, store bogstaver og korte sætninger — bruges til ~20 % af varm-op. */
export function buildWarmupAdvancedChunks(
  user: UserProfile,
  allowedKeys: string[],
): string[] {
  const chunks: string[] = [];
  const completed = user.completedTierIds;

  if (completed.includes('niveau-4')) {
    chunks.push(...niveau4AdvancedChunks(effectiveNiveau4Lexicon(user)));
  }
  if (completed.includes('niveau-5')) {
    chunks.push(...NIVEAU_5_MIXED_WORDS, ...NIVEAU_5_CONSOLIDATION_SENTENCES);
  }

  return filterWords([...new Set(chunks)], allowedKeys);
}

function highestCompletedTier(user: UserProfile): CurriculumTierId {
  for (let i = TIER_ORDER.length - 1; i >= 0; i--) {
    const tier = TIER_ORDER[i]!;
    if (user.completedTierIds.includes(tier)) return tier;
  }
  return 'grundniveau';
}

/** True once enough has been cleared that warm-up makes sense. */
export function isWarmupEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('grundniveau');
}

/** Build a one-round warm-up trin tailored to what the user has cleared. */
export function buildWarmupTrin(user: UserProfile): Trin | null {
  const used: WarmupSource[] = [];
  for (const tier of TIER_ORDER) {
    if (user.completedTierIds.includes(tier)) {
      const src = SOURCES[tier];
      if (src) used.push(src);
    }
  }
  if (used.length === 0) return null;

  const unlockedKeys = used[used.length - 1]!.unlockedKeys;
  const wordSources = used.map((s) => ({ chunks: s.chunks, weight: s.weight }));
  const advancedChunks = buildWarmupAdvancedChunks(user, unlockedKeys);
  const tierId = highestCompletedTier(user);
  const strictCase = user.completedTierIds.includes('niveau-5');

  return {
    id: WARMUP_TRIN_ID,
    tierId,
    trinNumber: 0,
    title: 'Daglig varm-op',
    description:
      advancedChunks.length > 0
        ? 'Blanding fra dine niveauer — ca. 20 % med tegn og store bogstaver.'
        : 'En kort blanding fra alle de niveauer du har gennemført.',
    goal: 'Hold det gamle stof varmt — uden at presse fart.',
    unlockedKeys,
    newKeys: [],
    ...(strictCase ? { strictCase: true } : {}),
    rounds: [
      round({
        id: `${WARMUP_TRIN_ID}-r1`,
        title: 'Bland alt det du har lært',
        purpose:
          advancedChunks.length > 0
            ? 'Ord fra dine niveauer plus tegnsætning og case i korte stykker.'
            : 'Tværgående ord-blanding fra dine ryddede niveauer.',
        mode: 'recall',
        generate: warmupBlended(
          wordSources,
          advancedChunks,
          90,
          WARMUP_ADVANCED_SHARE,
        ),
      }),
    ],
  };
}
